import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useMemo,
  useEffect,
} from 'react';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';
import api from '~/services/api';

const CatalogContext = createContext();

const CatalogProvider = ({ children }) => {
  const [restaurantInputs, setRestaurantInputs] = useState([]);
  const [restaurantIntermediaries, setRestaurantIntermediaries] = useState([]);
  const [categoriesDetails, setCategoriesDetails] = useState();
  const [complementCategories, setComplementCategories] = useState([]);
  const [suppliers, setSuppliers] = useState();
  const [banks, setBanks] = useState();
  const [categoriesOptions, setCategoriesOptions] = useState([]);
  const [complements, setComplements] = useState([]);

  const getInputs = useCallback(async () => {
    try {
      const response = await api.get('restaurants/inputs-minimal');
      setRestaurantInputs(response.data);
    } catch (err) {
      toast.error('Erro ao carregar insumos.');
      console.log(err.message);
    }
  }, []);

  const getIntermediaries = useCallback(async () => {
    try {
      const response = await api.get('restaurants/intermediary-minimal');
      setRestaurantIntermediaries(response.data);
    } catch (err) {
      toast.error('Erro ao carregar produtos intermediarios.');
    }
  }, []);

  const getCategoriesDetails = useCallback(async () => {
    try {
      const response = await api.get(
        '/restaurants/cash-flows/categories-detail'
      );

      const categoriesSelect = response?.data?.map((category) => {
        return {
          value: category.id,
          label: category.text,
        };
      });

      setCategoriesDetails(categoriesSelect);
    } catch (error) {
      toast.error('Erro ao baixar métodos de pagamento');
    }
  }, []);

  const getComplementCategories = useCallback(async () => {
    const response = await api.get('restaurants/product/complement-category');

    const data = response.data.map((categ) => ({
      value: categ.id,
      label: categ.name,
    }));

    setComplementCategories(data);
  }, []);

  const getSuppliers = useCallback(async () => {
    try {
      const response = await api.get('/restaurants/cash-flows/providers');

      const providersSelect = response?.data?.map((provider) => {
        return {
          value: provider.id,
          label: provider.name,
        };
      });

      setSuppliers(providersSelect);
    } catch (error) {
      toast.error('Erro ao baixar fornecedores');
    }
  }, []);

  const getBanks = useCallback(async () => {
    try {
      const response = await api.get('/restaurants/cash-flows/bank-accounts');

      const banksSelect = response?.data?.map((bank) => {
        return {
          value: bank.id,
          label: bank.name,
        };
      });

      setBanks(banksSelect);
    } catch (error) {
      toast.error('Erro ao baixar contas de banco');
    }
  }, []);

  const getCategories = useCallback(async () => {
    try {
      const response = await api.get('restaurants/product-category');

      const data = response.data.map((cat) => ({
        value: cat.id,
        label: cat.name,
      }));

      setCategoriesOptions(data);
    } catch (error) {
      toast.error('Erro ao baixar categorias');
    }
  }, []);

  const getComplements = useCallback(async () => {
    const response = await api.get('/restaurants/product/complements');

    const data = response.data.map((complement) => ({
      value: complement.id,
      label: complement.name,
    }));

    setComplements(data);
  }, []);

  const [fiscalGroups, setFiscalGroups] = useState([]);

  const getFiscalGroups = useCallback(async () => {
    try {
      const response = await api.get(`/restaurants/fiscal-groups`);
      const fiscal = response.data.map((group) => {
        return {
          label: group.title,
          value: group.id,
          id: group.id,
        };
      });
      setFiscalGroups(fiscal);
    } catch (error) {
      toast.error('Erro ao grupos fiscais');
    }
  }, []);

  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/delivery/orders') {
      getFiscalGroups();
    }
  }, [location.pathname, getFiscalGroups]);

  const pagesToFetchInputs = [
    '/products',
    '/products/',
    '/register/menu',
    '/register/menu/',
  ];

  useEffect(() => {
    if (pagesToFetchInputs.includes(location.pathname)) {
      getCategoriesDetails();
      getInputs();
      getIntermediaries();
      getSuppliers();
      getBanks();
      getComplementCategories();
      getCategories();
      getComplements();
    }
  }, [
    location.pathname,
    getCategoriesDetails,
    getInputs,
    getIntermediaries,
    getSuppliers,
    getBanks,
    getComplementCategories,
    getCategories,
    getComplements,
  ]);

  const value = useMemo(
    () => ({
      restaurantInputs,
      restaurantIntermediaries,
      getInputs,
      categoriesDetails,
      suppliers,
      getSuppliers,
      banks,
      getBanks,
      complementCategories,
      categoriesOptions,
      complements,
      getComplements,
      fiscalGroups,
    }),
    [
      restaurantInputs,
      restaurantIntermediaries,
      getInputs,
      categoriesDetails,
      suppliers,
      getSuppliers,
      banks,
      getBanks,
      complementCategories,
      categoriesOptions,
      complements,
      getComplements,
      fiscalGroups,
    ]
  );

  return (
    <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
  );
};

function useCatalog() {
  const context = useContext(CatalogContext);

  if (!context) {
    throw new Error('useNotify must be used within a NotifyProvider');
  }

  return context;
}

export { CatalogProvider, useCatalog };
